import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface ProjectTypeItem {
  id: number;
  name: string;
  countryId: number;
  countryName: string;
  createdDateTime?: Date;
  updatedDateTime?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectTypeService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/project-type`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Get all project types for dropdown (with search and country filter)
   */
  public getAllProjectTypesForDropdown(searchFilter?: string, countryId?: number, countryIds?: number[]): Observable<{ result: { id: number; name: string }[] }> {
    let queryParams = new HttpParams();
    if (searchFilter && searchFilter.trim() !== '') {
      queryParams = queryParams.set('searchFilter', searchFilter.trim());
    }
    // If countryIds array is provided, use it (takes precedence over single countryId)
    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    } else if (countryId !== undefined && countryId !== null) {
      // Fallback to single countryId for backward compatibility
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    return this.http
      .get<{ result: { id: number; name: string }[] }>(`${this.URL_PATH}/getProjectTypeByCountryId`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all project types with pagination
   */
  public getAll(
    pageIndex?: number,
    pageSize?: number,
    searchValue?: string,
    countryId?: number
  ): Observable<{ data: ProjectTypeItem[]; paginatorValue: { totalData: number; totalCount: number } }> {
    let params = new HttpParams();
    if (pageIndex !== undefined && pageIndex !== null) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<{ data: ProjectTypeItem[]; paginatorValue: { totalData: number; totalCount: number } }>(`${this.URL_PATH}/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Create a new project type
   */
  public create(name: string, countryId: number): Observable<{ message: string; result: ProjectTypeItem }> {
    return this.http
      .post<{ message: string; result: ProjectTypeItem }>(`${this.URL_PATH}/create`, { name, countryId })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Update an existing project type
   */
  public update(id: number, name: string, countryId: number): Observable<{ message: string; result: ProjectTypeItem }> {
    return this.http
      .put<{ message: string; result: ProjectTypeItem }>(`${this.URL_PATH}/update/${id}`, { name, countryId })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Delete a project type
   */
  public delete(id: number): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(`${this.URL_PATH}/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
}

